"use client";

import { cn } from "@/lib/utils";
import { LineChart, CheckCircle2, Cpu, Gauge } from "lucide-react";

interface Item {
  name: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  time: string;
}

let notifications = [
  {
    name: "Model Performance",
    description: "GPT-4 accuracy at 98.5%",
    time: "15m ago",
    icon: <LineChart className="h-5 w-5 text-primary" />,
    color: "bg-primary/5",
  },
  {
    name: "System Health",
    description: "All AI services operational",
    time: "10m ago",
    icon: <CheckCircle2 className="h-5 w-5 text-green-500" />,
    color: "bg-green-500/5",
  },
  {
    name: "Resource Usage",
    description: "GPU utilization at 75%",
    time: "5m ago",
    icon: <Cpu className="h-5 w-5 text-orange-500" />,
    color: "bg-orange-500/5",
  },
  {
    name: "API Status",
    description: "Response time: 150ms",
    time: "2m ago",
    icon: <Gauge className="h-5 w-5 text-blue-500" />,
    color: "bg-blue-500/5",
  },
];

notifications = Array.from({ length: 10 }, () => notifications).flat();

const Notification = ({ name, description, icon, color, time }: Item) => {
  return (
    <figure
      className={cn(
        "relative mx-auto min-h-fit w-full max-w-[400px] cursor-pointer overflow-hidden rounded-2xl p-4",
        // animation styles
        "transition-all duration-200 ease-in-out hover:scale-[103%]",
        // light styles
        "bg-white [box-shadow:0_0_0_1px_rgba(0,0,0,.03),0_2px_4px_rgba(0,0,0,.05),0_12px_24px_rgba(0,0,0,.05)]",
        // dark styles
        "transform-gpu dark:bg-transparent dark:backdrop-blur-md dark:[border:1px_solid_rgba(255,255,255,.1)] dark:[box-shadow:0_-20px_80px_-20px_#ffffff1f_inset]",
      )}
    >
      <div className="flex flex-row items-center gap-3">
        <div
          className={cn("flex size-10 items-center justify-center rounded-2xl", color)}
        >
          {icon}
        </div>
        <div className="flex flex-col overflow-hidden">
          <figcaption className="flex flex-row items-center whitespace-pre text-lg font-medium dark:text-white ">
            <span className="text-sm sm:text-lg">{name}</span>
            <span className="mx-1">·</span>
            <span className="text-xs text-gray-500">{time}</span>
          </figcaption>
          <p className="text-sm font-normal dark:text-white/60">
            {description}
          </p>
        </div>
      </div>
    </figure>
  );
};

export function AnimatedListDemo({
  className,
}: {
  className?: string;
}) {
  return (
    <div
      className={cn(
        "relative flex h-[500px] w-full flex-col overflow-hidden p-2",
        className,
      )}
    >
      <div className="animate-scroll space-y-4">
        {notifications.map((item, idx) => (
          <Notification {...item} key={idx} />
        ))}
      </div>

      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1/4 bg-gradient-to-t from-background"></div>
    </div>
  );
}
